import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';

//Layouts
import NonAuthLayout from '../Layouts/NonAuthLayout';
import VerticalLayout from '../Layouts/index';

//routes
import { authProtectedRoutes, publicRoutes } from './allRoutes';
import { AuthProtected } from './AuthProtected';
import Login from '../pages/Authentication/Login';
import { useDispatch, useSelector } from 'react-redux';
import ErrorPage from '../pages/AuthenticationInner/Errors/404ErrorPage';
import Callback from './callbackPage';
import { fetchPermissions } from '../permissionsSlice';
import { authConfig, UserManagerInstance } from '../Components/Common/functions/authConfig';
import LandingPage from '../pages/LandingPage/LandingPage';
import { getMLAppWindow } from '../Components/Utils/MLUtils';
import SignIn from '../pages/Authentication/Signin';
import { hasPermission, buildPermissionsMap } from '../Components/Utils/UtilityFunctions';
import { Loader } from '../Components/Utils/Loader';
import KeycloakCallback from './keycloakCallback';
import { keycloak } from '../Components/Common/functions/keycloakConfig';
import NotificationListener from '../Components/PushNotification/NotificationListener';

const Index = () => {
  // Check if environment is set in localStorage
  const [keycloakInitialized, setKeycloakInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const permissionsFromState = useSelector((state) => state.permissions.permissions);

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)])
  );


  const isEnvironmentSet = () => {
    const environment = localStorage.getItem('environment');
    return environment && environment.trim() !== '';
  };
  function clearAuth() {
    void UserManagerInstance.signoutRedirect({ post_logout_redirect_uri: authConfig.post_logout_redirect_uri })
    localStorage.removeItem('operatorname')
    localStorage.removeItem('environment');
  }

  useEffect(() => {
    const environment = localStorage.getItem('environment');
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');
    const isKeycloakCallback = location.pathname === '/keycloak/callback';
    const isSignInPage = location.pathname === '/signin';

    // 1. Clear session on signin page if already authenticated
    if (isSignInPage && environment?.toLowerCase() === 'keycloak' && keycloak.authenticated) {
      localStorage.clear();
      sessionStorage.clear();
      keycloak.logout({
        redirectUri: window.location.origin + '/signin'
      });
      return;
    }

    // 2. For callback page with code, we MUST initialize Keycloak
    if (isKeycloakCallback && hasCode) {

      // Check if environment is set - if not, this is an error
      if (!environment) {
        console.error(' No environment set - redirecting to signin');
        setKeycloakInitialized(true);
        navigate("/signin", { replace: true });
        return;
      }

      // Skip if already initialized AND authenticated
      if (keycloak.didInitialize && keycloak.authenticated) {
        setKeycloakInitialized(true);
        return;
      }

      // Initialize Keycloak to exchange code for token
      keycloak
        .init({
          onLoad: 'check-sso',
          pkceMethod: 'S256',
          checkLoginIframe: false,
          enableLogging: true,
        })
        .then((authenticated) => {
          setKeycloakInitialized(true);

          if (authenticated && keycloak.token) {
            localStorage.setItem("token", keycloak.token);
            localStorage.setItem("refreshToken", keycloak.refreshToken);
            localStorage.setItem("id_token", keycloak.idToken);

            if (keycloak.tokenParsed) {
              const userId = keycloak.tokenParsed.sub;
              const username = keycloak.tokenParsed.preferred_username || keycloak.tokenParsed.email;
              if (userId) localStorage.setItem("userId", userId);
              if (username) localStorage.setItem("userName", username);
            }
          } else {
            console.error('Not authenticated after init');
          }
        })
        .catch((error) => {
          console.error('Keycloak init failed:', error);
          setKeycloakInitialized(true);
          setTimeout(() => navigate("/signin", { replace: true }), 1000);
        });

      return; // Important: stop here for callback route
    }

    // 3. For all other routes, just mark as initialized
    setKeycloakInitialized(true);

  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!keycloakInitialized || !keycloak.authenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      keycloak.updateToken(600000)
        .then(refreshed => {
          if (refreshed) {
            localStorage.setItem("token", keycloak.token);
            localStorage.setItem("refreshToken", keycloak.refreshToken);
          }
        })
        .catch(() => {
          console.error("Token refresh failed");
          keycloak.logout();
        });
    }, 500000);

    return () => clearInterval(intervalId);
  }, [keycloakInitialized]);

  const checkAccessTokenAndRenew = async () => {
    const user = await UserManagerInstance.getUser()
    const userName = localStorage.getItem("userName")
    if (user?.expires_in && user?.expires_in <= Date.now()) {
      try {
        UserManagerInstance.signinSilent().then((userObj) => {
          if (userObj) {
            localStorage.setItem("token", userObj.access_token);
            localStorage.setItem("refreshToken", userObj.refresh_token)
            if (localStorage.getItem("redirectedToUMP") === "true") {
              const mlWindow = getMLAppWindow();
              const mlOrigin = new URL(window.env.REACT_APP_ML_REDIRECT_URL).origin;
              mlWindow?.postMessage({ token: userObj.access_token, refreshToken: userObj.refresh_token, redirectedFromUMP: "true",userName: userName }, mlOrigin);
            }
          }
        })
      } catch (error) {
        console.error("Error renewing access token:", error);
      }
    }
  }

  useEffect(() => {
    const tokenCheckInterval = setInterval(checkAccessTokenAndRenew, 600000) // Check every 10 minutes

    return () => {
      clearInterval(tokenCheckInterval)
    }
  }, [])

  useEffect(() => {
    const user = UserManagerInstance.getUser()
    if (user?.expires_in && user?.expires_in <= Date.now()) {
      clearAuth()
    }
  }, [])

  UserManagerInstance.events.addAccessTokenExpiring(() => {
    // Perform the background token refresh
    void UserManagerInstance.signinSilent().then((user) => {
    })
  })

  useEffect(() => {
    const route = authProtectedRoutes.find(route => route.path === location.pathname);
    if (route) {
      document.title = route.title || "Elytra";
    } else {
      document.title = "Elytra";
    }
  }, [location.pathname]);

  let permissions, parsedPermissions;
  const environmentFromStorage = localStorage.getItem('environment');

  if (environmentFromStorage?.toLowerCase() === "native") {
    // This Below Changed When Login Response Will be Mapped Correctly 
    // permissions = localStorage.getItem('permissionss');
    // parsedPermissions = permissions ? buildPermissionsMap(JSON.parse(permissions)) : new Map();
    permissions = permissionsMap;
    parsedPermissions = permissionsMap ? permissionsMap : new Map();
  } else {
    permissions = permissionsMap;
    parsedPermissions = permissionsMap ? permissionsMap : new Map();
  }
  const hasDashboardPermission = hasPermission(parsedPermissions, "dashboard", "dashboard")
  const hasCustomerPermission = hasPermission(parsedPermissions, "customer", "customer")

  const dispatch = useDispatch();
  const roleName = localStorage.getItem('roleName')
  const [permissionsFetched, setPermissionsFetched] = useState(false);
  const [authenticated, setAuthenticated] = useState(null);
  const operatorConfiguration = useSelector((state) => state?.GetOperatorSpecificConfiguration?.OperatorSpecificConfiguration)
  const LLMPermission = operatorConfiguration?.configurations?.LLM?.value?.LLMEnabled?.value === true ? true : false
  useEffect(() => {
    dispatch(fetchPermissions())
      .then(() => setPermissionsFetched(true))
      .catch(() => setPermissionsFetched(true));
  }, [dispatch]);

  useEffect(() => {
  if (
    "Notification" in window &&
    Notification.permission === "default"
  ) {
    Notification.requestPermission();
  }
}, []);


  if (!permissionsFetched) {
    return null;
  }
  // const signIn = useSelector((state) => state.SignIn.signin);
  // console.log("signIn",signIn)
  return (
    <React.Fragment >
       <NotificationListener />
      <Routes>
        <Route
          path="/login/:id"
          element={
            isEnvironmentSet() ? (
              <Login userManager={UserManagerInstance} authConfig={authConfig} />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/" element={<Navigate to={"/home"} replace />} />
        <Route path="/keycloak/callback" element={<KeycloakCallback />} />
        <Route
          path="/*"
          element={
            <Navigate
              to={
                !localStorage.getItem("token")
                  ? "/home"
                  : hasDashboardPermission
                    ? "/dashboard"
                    : hasCustomerPermission
                      ? "/customer"
                      : "/operator-listing"
              }
              replace
            />
          }
        />
        <Route path="/home" element={<LandingPage />} />

        {/* <Route path="/" element={<Login userManager={UserManagerInstance} />} /> This is for direct navigating to the account page */}
        <Route path="/callback" element={<Callback userManager={UserManagerInstance} setAuth={setAuthenticated} />} />
        <Route>
          {publicRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={<NonAuthLayout>{route.component}</NonAuthLayout>}
              key={idx}
              exact={true}
            />
          ))}
        </Route>

        {permissions && (
          <Route>
            {authProtectedRoutes.map((route, idx) => {
              const resourceName = route?.path?.startsWith('/')
                ? route.path.substring(1)
                : route.path;

              const hasPermissions =
                (parsedPermissions instanceof Map) &&
                [...parsedPermissions.keys()].some(key => key.includes(resourceName));

              const isCreateCustomerRoute =
                route.path === '/create-customer' &&
                hasPermission(parsedPermissions, "customer", "customer", "create")
              const isAICustomerRoute =
                route.path === '/chatbot' &&
                LLMPermission;
              const isUploadDocumentRoute =
                route.path === '/documents' &&
                hasPermission(parsedPermissions, "miscellaneous", "document", "list") && roleName === 'Customer';
              const isListOperatorRoute =
                route.path === '/operator-listing' &&
                hasPermission(parsedPermissions, "operators", "operator", "list")
              const isCreateOperatorRoute =
                route.path === '/create-operator' &&
                hasPermission(parsedPermissions, "operators", "operator", "create")
              const isProfileOperatorRoute =
                route.path === '/business-profile' &&
                hasPermission(parsedPermissions, "administration", "profileManagement", "view")
              const isCreateConfigurationsRoute =
                route.path === '/create-configurations' &&
                hasPermission(parsedPermissions, "administration", "configurations", "create") 
              const isEditOperatorRoute =
                route.path === '/edit-operator/:id' &&
                hasPermission(parsedPermissions, "operators", "operator", "update")
              const isViewOperatorRoute =
                route.path === '/view-operator/:id' &&
                hasPermission(parsedPermissions, "operators", "operator", "view")
              const isDashboardRoute =
                route.path === '/dashboard' &&
                hasPermission(parsedPermissions, "dashboard", "dashboard")
              const isSubscriptionRoute =
                route.path === '/subscription' &&
                hasPermission(parsedPermissions, "customer", "subscription", "view") && (roleName === 'Customer'
                );
              const isInvoicesRoute =
                route.path === '/invoices' &&
                hasPermission(parsedPermissions, "customer", "invoice", "list")
              const isChangePlanRoute =
                route.path === '/change-offer/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isChangePlanForSelfcareRoute =
                route.path === '/selfcare-changeplan/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create") || hasPermission(parsedPermissions, "customer", "subscription", "view"))
              const isAddOnForSelfcareRoute =
                route.path === '/selfcare-addon/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create") || hasPermission(parsedPermissions, "customer", "subscription", "view"))
              const isChangePlanSummaryRoute =
                route.path === '/change-offer-summary/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isOrderRoute =
                route.path === '/order-details' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isFilterRoute =
                route.path === '/order-filtering/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isSelectRoute =
                route.path === '/order-select' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isOrderListingRoute =
                route.path === '/orders' &&
                hasPermission(parsedPermissions, "customer", "order", "list")
              const isOrderViewRoute =
                route.path === '/view-order/:id' &&
                hasPermission(parsedPermissions, "customer", "order", "view")
              const isAddOnRoute =
                route.path === '/add-on/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "create") || hasPermission(parsedPermissions, "customer", "order", "create"))
              const isCancelOrderRoute =
                route.path === '/cancel-order/:id' &&
                (
                  hasPermission(parsedPermissions, "customer", "subscription", "unsubscribe") || hasPermission(parsedPermissions, "customer", "order", "delete"))
              const isLandingPage =
                route.path === '/home';
              const isUpdateCustomerRoute =
                route.path === '/update-customer/:id' &&
                hasPermission(parsedPermissions, "customer", "customer", "update")

              const isEditStationRoute =
                route.path === '/station/:id' &&
                hasPermission(parsedPermissions, "station", "station", "update")
              const isViewStationRoute =
                route.path === '/view-station/:id' &&
                hasPermission(parsedPermissions, "station", "station", "view")
              const isCreateStationRoute =
                route.path === '/create-station' &&
                hasPermission(parsedPermissions, "station", "station", "create")
              const isCreateResourceRoute =
                route.path === '/create-resources' &&
                hasPermission(parsedPermissions, "pricing", "resource", "create")
              const isEditResourceRoute =
                route.path === '/edit-resource/:id' &&
                hasPermission(parsedPermissions, "pricing", "resource", "update")
              const isCreateUomRoute =
                route.path === '/create-unit' &&
                hasPermission(parsedPermissions, "pricing", "uom", "create")
              const isEditUomRoute =
                route.path === '/edit-uom/:id' &&
                hasPermission(parsedPermissions, "pricing", "uom", "update")
              const isCreateAttributeRoute =
                route.path === '/create-attribute' &&
                hasPermission(parsedPermissions, "pricing", "attribute", "create")
              const isAttributeRoute =
                route.path === '/attribute' &&
                hasPermission(parsedPermissions, "pricing", "attribute", "list")
              const isUpdateAttributeRoute =
                route.path === '/update-attribute/:id' &&
                hasPermission(parsedPermissions, "pricing", "attribute", "update")
              const isServiceRoute =
                route.path === '/create-service' &&
                hasPermission(parsedPermissions, "pricing", "service", "create")
              const isEditServiceRoute =
                route.path === '/edit-service/:id' &&
                hasPermission(parsedPermissions, "pricing", "service", "update")
              const isCloneServiceRoute =
                route.path === '/clone-service/:id' &&
                hasPermission(parsedPermissions, "pricing", "service", "clone")
              const isCreateOfferRoute =
                route.path === '/create-offer' &&
                hasPermission(parsedPermissions, "pricing", "offer", "create")
              const isImportDataRoute =
                route.path === '/import-data' &&
                hasPermission(parsedPermissions, "pricing", "offer", "import")
              const isUpdateOfferRoute =
                route.path === '/update-offer/:id' &&
                hasPermission(parsedPermissions, "pricing", "offer", "update")
              const isViewOfferRoute =
                route.path === '/view-offer/:id' &&
                hasPermission(parsedPermissions, "pricing", "offer", "view")
              const isCloneOfferRoute =
                route.path === '/clone-offer/:id' &&
                hasPermission(parsedPermissions, "pricing", "offer", "clone")
              const isSimulateRoute =
                route.path === '/simulate/:id' &&
                hasPermission(parsedPermissions, "customer", "customer", "simulate")
              const isSessionsRoute =
                route.path === '/sessions/:id' &&
                hasPermission(parsedPermissions, "customer", "customer")
              const isViewCustomerRoute =
                route.path === '/view-customer/:id' &&
                hasPermission(parsedPermissions, "customer", "customer", "view")
              const isCreateReservationRoute =
                route.path === '/create-reservation' &&
                hasPermission(parsedPermissions, "reservation", "reservation", "create")
              const isCreateReservationRouteID =
                route.path === '/create-reservation-customer/:id' &&
                hasPermission(parsedPermissions, "reservation", "reservation", "create")
              const isUpadateReservationRoute =
                route.path === '/update-reservation/:id' &&
                hasPermission(parsedPermissions, "reservation", "reservation", "update")
              const isViewReservationRoute =
                route.path === '/view-reservation/:id' &&
                hasPermission(parsedPermissions, "reservation", "reservation", "view")
              const isPartnerListing =
                route.path === '/partner-listing' &&
                hasPermission(parsedPermissions, "partners", "partner", "list")
              const isCreatePartner =
                route.path === '/create-partner' &&
                hasPermission(parsedPermissions, "partners", "partner", "create")
              const isUpdatePartner =
                route.path === '/update-partner/:id' &&
                hasPermission(parsedPermissions, "partners", "partner", "update")
              const isViewPartner =
                route.path === '/view-partner/:id' &&
                hasPermission(parsedPermissions, "partners", "partner", "view")
              const isPolicyListingRoute =
                route.path === '/policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "view")
              const isPartnerForPolicyRoute =
                route.path === '/partner-for-policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "create")
              const isUpdatePartnerForPolicyRoute =
                route.path === '/update-partner-for-policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "update")
              const isCreatePolicyRoute =
                route.path === '/create-policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "create")
              const isUpdatePolicyRoute =
                route.path === '/update-policy/:id' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "update")
              const isOfferForPolicyRoute =
                route.path === '/offer-for-policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "create")
              const isUpdateOfferForPolicyRoute =
                route.path === '/update-offer-for-policy' &&
                hasPermission(parsedPermissions, "partners", "commissionPolicy", "update")
              const isEditDeviceRoute =
                route.path === '/update-device/:deviceId' &&
                hasPermission(parsedPermissions, "device", "device", "update")
              const isListDeviceRoute =
                route.path === '/devices' &&
                hasPermission(parsedPermissions, "device", "device", "list")
                const isViewDeviceRoute=route.path === '/view-device/:deviceId' &&
                hasPermission(parsedPermissions, "device", "device", "view")
              const isCreateDeviceRoute =
                route.path === '/create-device' &&
                hasPermission(parsedPermissions, "device", "device", "create")
              const isOrderEnabled = operatorConfiguration?.configurations?.orderManagement?.value?.OrderEnabled?.value === true;

              const isLightWeightOrder =
                route.path === '/customer/order' &&
                hasPermission(parsedPermissions, "customer", "order", "view") && roleName === 'Customer';


              const isLightWeightInvoice =
                route.path === '/customer/invoice' &&
                hasPermission(parsedPermissions, "customer", "invoice", "view") && (roleName === 'Customer'
                );
              const isLightWeightPayment =
                route.path === '/customer/payment'
                && hasPermission(parsedPermissions, "customer", "payment", "view")
                && (roleName === 'Customer'
                );
              const isLightWeightProfile =
                route.path === '/customer/profile' &&
                hasPermission(parsedPermissions, "administration", "profileManagement", "view") && hasPermission(parsedPermissions, "administration", "profileManagement", "update") && (roleName === 'Customer'
                );
              const isPolicyListRoutes =
                route.path === '/policies' &&
                hasPermission(parsedPermissions, "charging", "policy", "list") && hasPermission(parsedPermissions, "pricing", "offer", "create")
              const isCreatePolicyRoutes =
                route.path === '/add-policy' &&
                hasPermission(parsedPermissions, "charging", "policy", "create") && hasPermission(parsedPermissions, "pricing", "offer", "create")
              const isUpdatePoliciesRoute =
                route.path === '/edit-policies/:id' &&
                hasPermission(parsedPermissions, "charging", "policy", "update") && hasPermission(parsedPermissions, "pricing", "offer", "create")
              const isUserManagementListingRoute =
                route.path === '/user' &&
                hasPermission(parsedPermissions, "administration", "userManagement", "list")
              const isUserManagementCreateRoute =
                route.path === '/add-user' &&
                hasPermission(parsedPermissions, "administration", "userManagement", "create")
              const isUpdateUserRoute =
                route.path === '/edit-user/:id' &&
                hasPermission(parsedPermissions, "administration", "userManagement", "update")
              const isViewUserRoute =
                route.path === '/view-user/:id' &&
                hasPermission(parsedPermissions, "administration", "userManagement", "view")
              const isProfileUserRoute =
                route.path === '/profile' &&
                hasPermission(parsedPermissions, "administration", "profileManagement", "update")
              const isTaskRoute =
                route.path === '/tasks' &&
                hasPermission(parsedPermissions, "customer", "task", "list")
              const isQuotaManagementRoute =
                route.path === '/quotamanagement' &&
                hasPermission(parsedPermissions, "charging", "quota", "list")
              const isRatingGroupRoute =
                route.path === '/ratinggroup' &&
                hasPermission(parsedPermissions, "charging", "ratinggroup", "list")
              const isPeerConfigurationRoute =
                route.path === '/peerconfiguration' &&
                hasPermission(parsedPermissions, "charging", "peerConfigurations", "list")
              const isMidSessionRoute =
                route.path === '/midsession' &&
                hasPermission(parsedPermissions, "charging", "midsessioncdr", "list")
              const isUserRoleManagement = route.path === "/userroles" && hasPermission(parsedPermissions, "administration", "roles", "list")
              const isUserRoleCreateRoute =
                route.path === '/add-userrole' &&
                hasPermission(parsedPermissions, "administration", "roles", "create")
              const isUserRoleUpdateRoute =
                route.path === '/edit-userrole/:name' &&
                hasPermission(parsedPermissions, "administration", "roles", "update")
              const isUserRoleViewRoute =
                route.path === '/view-userrole/:name' &&
                hasPermission(parsedPermissions, "administration", "roles", "view")
              const isReportRoute = route.path === '/report' && hasPermission(parsedPermissions, "reports", "reports")
              const isFinRevReportRoute = route.path === '/finance-report' && hasPermission(parsedPermissions, "reports", "reports")
              const isOperationalReportRoute = route.path === '/operational-report' && hasPermission(parsedPermissions, "reports", "reports")
              const isEMSReportRoute = route.path === '/reports' && hasPermission(parsedPermissions, "EMSReports", "emsreports")

              //New
              const isCategoryRoute =
                route.path === '/category' &&
                hasPermission(parsedPermissions, "configuration", "category")
                const isItemsRoute =
                route.path === '/items' &&
                hasPermission(parsedPermissions, "configuration", "item","view")
                const isLocationsRoute =
                route.path === '/locations' &&
                hasPermission(parsedPermissions, "configuration", "location")
              const isInventoryRoute =
                route.path === '/inventory' &&
                hasPermission(parsedPermissions, "monitoringAndControl", "inventory")
                  const isPricebookRoute =
                route.path === '/pricebook' &&
                hasPermission(parsedPermissions, "configuration", "pricebook")
                const isTransferRoute =
                route.path === '/stocktransfer' &&
                hasPermission(parsedPermissions, "transactions", "transfer")
                 const isPORoute =
                route.path === '/purchaseorder' &&
                hasPermission(parsedPermissions, "transactions", "purchaseorder")
                 const isSORoute =
                route.path === '/salesorder' &&
                hasPermission(parsedPermissions, "transactions", "salesorder")
                  const isAdjustmentRoute =
                route.path === '/adjustment' &&
                hasPermission(parsedPermissions, "transactions", "adjustment")
                   const isNotificationsRoute =
                route.path === '/notifications' &&
                hasPermission(parsedPermissions, "monitoringAndControl", "notifications")
              const isStepTrackingRoute = route.path === '/step-tracking'
              const isTransactionsRoute = route.path === '/transactions'
              const isOffersRoute = route.path === '/offers'
              const isOffersFormRoute = route.path === '/offer-create'
              const isChargingSessionReportRoute = route.path === '/charging-session-report'
              const isBusinessPerformanceReportRoute = route.path === '/business-performance-report'
              if (
                hasPermissions ||
                isEditUomRoute ||
                isEditResourceRoute ||
                isListOperatorRoute ||
                isCreateOperatorRoute ||
                isEditOperatorRoute ||
                isUpdatePartner ||
                isCreatePartner ||
                isPartnerListing ||
                isCreatePolicyRoute ||
                isPartnerForPolicyRoute ||
                isOfferForPolicyRoute ||
                isCreateReservationRouteID ||
                isCreateReservationRoute ||
                isUpadateReservationRoute ||
                isViewReservationRoute ||
                isCreateCustomerRoute ||
                isSimulateRoute ||
                isSessionsRoute ||
                isInvoicesRoute ||
                isCreateResourceRoute ||
                isCreateUomRoute ||
                isServiceRoute ||
                isCreateOfferRoute ||
                isUpdateCustomerRoute ||
                isEditStationRoute ||
                isViewStationRoute ||
                isCreateStationRoute ||
                isViewCustomerRoute ||
                isOrderRoute ||
                isFilterRoute ||
                isSelectRoute ||
                isOrderListingRoute ||
                isPolicyListingRoute ||
                isViewPartner ||
                isEditDeviceRoute ||
                isViewDeviceRoute ||
                isCreateDeviceRoute ||
                isOrderViewRoute ||
                isCancelOrderRoute ||
                isUpdatePolicyRoute ||
                isUpdateOfferForPolicyRoute ||
                isUpdatePartnerForPolicyRoute ||
                isLightWeightOrder ||
                isLightWeightInvoice ||
                isLightWeightPayment ||
                isLightWeightProfile ||
                isUpdateOfferRoute ||
                isViewOfferRoute ||
                isImportDataRoute ||
                isCreateAttributeRoute ||
                isUpdateAttributeRoute ||
                isAttributeRoute ||
                isDashboardRoute ||
                isPolicyListRoutes ||
                isCreatePolicyRoutes ||
                isUpdatePoliciesRoute ||
                isViewOperatorRoute ||
                isUserManagementListingRoute ||
                isUserManagementCreateRoute ||
                isUpdateUserRoute ||
                isViewUserRoute ||
                isAddOnRoute ||
                isAICustomerRoute ||
                isChangePlanRoute ||
                isChangePlanSummaryRoute ||
                isTaskRoute ||
                isProfileUserRoute ||
                isProfileOperatorRoute ||
                isCloneOfferRoute ||
                isCloneServiceRoute ||
                isSubscriptionRoute ||
                isEditServiceRoute ||
                isUploadDocumentRoute ||
                isLandingPage ||
                isChangePlanForSelfcareRoute ||
                isAddOnForSelfcareRoute ||
                isCreateConfigurationsRoute ||
                isQuotaManagementRoute ||
                isRatingGroupRoute ||
                isPeerConfigurationRoute ||
                isMidSessionRoute ||
                isListDeviceRoute ||
                isUserRoleManagement || 
                isUserRoleCreateRoute || 
                isUserRoleUpdateRoute || 
                isUserRoleViewRoute ||
                isReportRoute ||
                isEMSReportRoute ||
                //New
                isCategoryRoute ||
                isItemsRoute ||
                isLocationsRoute ||
                isInventoryRoute ||
                isPricebookRoute ||
                isTransferRoute ||
                isPORoute ||
                isSORoute || 
                isAdjustmentRoute ||
                isNotificationsRoute ||
                isStepTrackingRoute ||
                isFinRevReportRoute ||
                isOperationalReportRoute ||

                isTransactionsRoute ||
                isOffersRoute ||
                isOffersFormRoute ||
                isChargingSessionReportRoute ||
                isBusinessPerformanceReportRoute
              ) {
                return (
                  <Route
                    path={route.path}
                    element={
                      <AuthProtected>
                        <VerticalLayout>{route.component}</VerticalLayout>
                      </AuthProtected>
                    }
                    key={idx}
                    exact={true}
                  />
                );
              } else
                return localStorage.getItem("token") ? (
                  <Route
                    path={route.path}
                    element={
                      <VerticalLayout>
                        {/* <Navigate to="/404-Error" /> */}
                        {permissions && permissions.size>0?
                        <ErrorPage></ErrorPage>:<Loader></Loader>}
                      </VerticalLayout>
                    }
                    key={idx}
                  />
                ) : (
                  <Route path="/home" element={<LandingPage />} />
                );
            })}
          </Route>
        )}
      </Routes>
    </React.Fragment>
  );
};

export default Index;